import React, { useEffect, useState, useCallback } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import accessLocalStorage from '../../services/accessLocalStorage';
import './config-styles.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const getApiBase = async () => {
  const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
  if (envBase) return envBase;
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const chartColors = {
  primary: 'rgb(59, 130, 246)',
  success: 'rgb(34, 197, 94)',
  danger: 'rgb(239, 68, 68)',
  warning: 'rgb(245, 158, 11)',
  purple: 'rgb(139, 92, 246)',
  gray: 'rgb(107, 114, 128)',
};

const chartColorsAlpha = {
  primary: 'rgba(59, 130, 246, 0.5)',
  success: 'rgba(34, 197, 94, 0.5)',
  danger: 'rgba(239, 68, 68, 0.5)',
  warning: 'rgba(245, 158, 11, 0.5)',
  purple: 'rgba(139, 92, 246, 0.5)',
  gray: 'rgba(107, 114, 128, 0.5)',
};

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [kpis, setKpis] = useState({});
  const [queueMetrics, setQueueMetrics] = useState([]);
  const [period, setPeriod] = useState('7');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();

      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(period) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const [historyRes, kpisRes, queuesRes] = await Promise.all([
        fetch(`${apiBase}/v2/analytics/history/${tenantId}/queues?start_date=${startDate}&end_date=${endDate}&granularity=daily`),
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/kpis`),
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/queues`),
      ]);

      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistoryData(data.data || []);
      }

      if (kpisRes.ok) {
        const data = await kpisRes.json();
        setKpis(data);
      }

      if (queuesRes.ok) {
        const data = await queuesRes.json();
        setQueueMetrics(data.queues || []);
      }
    } catch (e) {
      console.error('Erro ao carregar dados do dashboard:', e);
      setError('Erro ao carregar dados do dashboard. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const aggregateByDate = (data) => {
    const grouped = {};
    data.forEach((row) => {
      const date = row.date?.split('T')[0] || row.date;
      if (!grouped[date]) {
        grouped[date] = { 
          offered: 0, 
          answered: 0, 
          abandoned: 0,
          wait_time: 0,
          talk_time: 0,
          count: 0
        };
      }
      grouped[date].offered += row.calls_offered || 0;
      grouped[date].answered += row.calls_answered || 0;
      grouped[date].abandoned += row.calls_abandoned || 0;
      grouped[date].wait_time += row.total_wait_time || 0;
      grouped[date].talk_time += row.total_talk_time || 0;
      grouped[date].count++;
    });
    return grouped;
  };

  const aggregated = aggregateByDate(historyData);
  const dates = Object.keys(aggregated).sort();
  const labels = dates.map(d => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  });

  const callsChartData = {
    labels,
    datasets: [
      {
        label: 'Atendidas',
        data: dates.map(d => aggregated[d].answered),
        borderColor: chartColors.success,
        backgroundColor: chartColorsAlpha.success,
        tension: 0.3,
      },
      {
        label: 'Abandonadas',
        data: dates.map(d => aggregated[d].abandoned),
        borderColor: chartColors.danger,
        backgroundColor: chartColorsAlpha.danger,
        tension: 0.3,
      },
    ],
  };

  const avgWaitTimes = dates.map(d => {
    const agg = aggregated[d];
    return agg.answered > 0 ? Math.round(agg.wait_time / agg.answered) : 0;
  });

  const avgTalkTimes = dates.map(d => {
    const agg = aggregated[d];
    return agg.answered > 0 ? Math.round(agg.talk_time / agg.answered) : 0;
  });

  const timesChartData = {
    labels,
    datasets: [
      {
        label: 'Tempo Medio Espera (s)',
        data: avgWaitTimes,
        borderColor: chartColors.warning,
        backgroundColor: chartColorsAlpha.warning,
        tension: 0.3,
      },
      {
        label: 'Tempo Medio Atendimento (s)',
        data: avgTalkTimes,
        borderColor: chartColors.primary,
        backgroundColor: chartColorsAlpha.primary,
        tension: 0.3,
      },
    ],
  };

  const totalOffered = dates.reduce((sum, d) => sum + aggregated[d].offered, 0);
  const totalAnswered = dates.reduce((sum, d) => sum + aggregated[d].answered, 0);
  const totalAbandoned = dates.reduce((sum, d) => sum + aggregated[d].abandoned, 0);

  const distributionData = {
    labels: ['Atendidas', 'Abandonadas'],
    datasets: [
      {
        data: [totalAnswered, totalAbandoned],
        backgroundColor: [chartColors.success, chartColors.danger],
        borderWidth: 0,
      },
    ],
  };

  const queueBarData = {
    labels: queueMetrics.slice(0, 10).map(q => q.queue_name || q.queue_key),
    datasets: [
      {
        label: 'Atendidas',
        data: queueMetrics.slice(0, 10).map(q => q.calls_answered || 0),
        backgroundColor: chartColors.success,
      },
      {
        label: 'Abandonadas',
        data: queueMetrics.slice(0, 10).map(q => q.calls_abandoned || 0),
        backgroundColor: chartColors.danger,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        stacked: false,
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  return (
    <div className="ccv2-page-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Dashboard</h1>
          <p>Visao geral e tendencias do Call Center</p>
        </div>
        <div className="ccv2-page-actions">
          <select
            className="ccv2-period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="7">Ultimos 7 dias</option>
            <option value="14">Ultimos 14 dias</option>
            <option value="30">Ultimos 30 dias</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="ccv2-error-banner" style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          margin: '16px 0',
          color: '#991b1b',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="ccv2-config-loading">
          <div className="ccv2-spinner"></div>
        </div>
      ) : (
        <>
          <div className="ccv2-dashboard-kpis">
            <div className="ccv2-dashboard-kpi">
              <div className="ccv2-dashboard-kpi-value">{totalOffered}</div>
              <div className="ccv2-dashboard-kpi-label">Total Oferecidas</div>
            </div>
            <div className="ccv2-dashboard-kpi">
              <div className="ccv2-dashboard-kpi-value ccv2-kpi-green">{totalAnswered}</div>
              <div className="ccv2-dashboard-kpi-label">Total Atendidas</div>
            </div>
            <div className="ccv2-dashboard-kpi">
              <div className="ccv2-dashboard-kpi-value ccv2-kpi-red">{totalAbandoned}</div>
              <div className="ccv2-dashboard-kpi-label">Total Abandonadas</div>
            </div>
            <div className="ccv2-dashboard-kpi">
              <div className={`ccv2-dashboard-kpi-value ${(kpis.service_level || 0) >= 80 ? 'ccv2-kpi-green' : 'ccv2-kpi-red'}`}>
                {(kpis.service_level || 0).toFixed(1)}%
              </div>
              <div className="ccv2-dashboard-kpi-label">SLA</div>
            </div>
            <div className="ccv2-dashboard-kpi">
              <div className={`ccv2-dashboard-kpi-value ${(kpis.abandon_rate || 0) <= 5 ? 'ccv2-kpi-green' : 'ccv2-kpi-red'}`}>
                {(kpis.abandon_rate || 0).toFixed(1)}%
              </div>
              <div className="ccv2-dashboard-kpi-label">Taxa Abandono</div>
            </div>
          </div>

          <div className="ccv2-dashboard-grid">
            <div className="ccv2-dashboard-card ccv2-dashboard-card-large">
              <h3>Chamadas por Dia</h3>
              <div className="ccv2-chart-container">
                <Line data={callsChartData} options={lineOptions} />
              </div>
            </div>

            <div className="ccv2-dashboard-card">
              <h3>Distribuicao</h3>
              <div className="ccv2-chart-container-small">
                <Doughnut data={distributionData} options={doughnutOptions} />
              </div>
            </div>

            <div className="ccv2-dashboard-card ccv2-dashboard-card-large">
              <h3>Tempos Medios por Dia</h3>
              <div className="ccv2-chart-container">
                <Line data={timesChartData} options={lineOptions} />
              </div>
            </div>

            <div className="ccv2-dashboard-card ccv2-dashboard-card-large">
              <h3>Chamadas por Fila</h3>
              <div className="ccv2-chart-container">
                <Bar data={queueBarData} options={barOptions} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
